import React, { useState } from "react";
import { Menu } from "@headlessui/react";
import { CheckIcon, ChevronDownIcon } from "@heroicons/react/20/solid";
import clsx from "clsx";
import { Float } from "@headlessui-float/react";
import { ToolTip } from "../tooltip";

const POLARIS_SELECT_MENU_CLASS = "polarisSelectMenu";

/**
 * Returns true if the event is a mouse click event and the click target is a <SelectMenu>.
 */
export const isEventClickTargetOnSelectMenu = (e: React.MouseEvent<HTMLElement>) => {
  if (e.type !== "click" || !e.target) {
    return false;
  }

  return (e.target as HTMLElement).closest(`.${POLARIS_SELECT_MENU_CLASS}`);
};

export interface SelectMenuStandardItem {
  label: string;
  value: string; // must be string to be compatible with standard HTML <select>
}

export const LightbulbIcon = (
  <svg width="16" height="16" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M25.1 11.8667L23.6333 8.73332L20.5 7.26666L23.6333 5.79999L25.1 2.66666L26.5667 5.79999L29.7 7.26666L26.5667 8.73332L25.1 11.8667ZM27.9 21.5L26.908 19.392L24.8 18.4L26.908 17.408L27.9 15.3L28.892 17.408L31 18.4L28.892 19.392L27.9 21.5Z"
      fill="#3C1DA5"
    />
    <path
      d="M11.6667 30.6667C11.0667 30.6667 10.5389 30.5222 10.0833 30.2333C9.62778 29.9444 9.33333 29.5444 9.2 29.0333H8.73333C8.2 29.0333 7.73333 28.8333 7.33333 28.4333C6.93333 28.0333 6.73333 27.5667 6.73333 27.0333V22.2667C5.26667 21.3111 4.11111 20.0889 3.26667 18.6C2.42222 17.1111 2 15.4667 2 13.6667C2 10.9778 2.93889 8.69444 4.81667 6.81667C6.69444 4.93889 8.97778 4 11.6667 4C14.3556 4 16.6389 4.93889 18.5167 6.81667C20.3944 8.69444 21.3333 10.9778 21.3333 13.6667C21.3333 15.4667 20.9111 17.1111 20.0667 18.6C19.2222 20.0889 18.0667 21.3111 16.6 22.2667V27.0333C16.6 27.5667 16.4 28.0333 16 28.4333C15.6 28.8333 15.1333 29.0333 14.6 29.0333H14.1333C14 29.5444 13.7056 29.9444 13.25 30.2333C12.7944 30.5222 12.2667 30.6667 11.6667 30.6667ZM8.73333 27.0333H14.6V25.5667H8.73333V27.0333ZM8.73333 24.2333H14.6V22.9H8.73333V24.2333ZM8.43333 20.9H10.9V16.3333L7.83333 13.2667L8.86667 12.2333L11.6667 15.0333L14.4667 12.2333L15.5 13.2667L12.4333 16.3333V20.9H14.9C16.2333 20.2778 17.3056 19.3111 18.1167 18C18.9278 16.6889 19.3333 15.2444 19.3333 13.6667C19.3333 11.5111 18.5944 9.69444 17.1167 8.21667C15.6389 6.73889 13.8222 6 11.6667 6C9.51111 6 7.69444 6.73889 6.21667 8.21667C4.73889 9.69444 4 11.5111 4 13.6667C4 15.2444 4.40556 16.6889 5.21667 18C6.02778 19.3111 7.1 20.2778 8.43333 20.9Z"
      fill="#3C1DA5"
    />
  </svg>
);

export function SelectMenuStandard({
  id,
  items,
  onSelect,
  selectedValue,
  placeholder,
  height,
}: {
  id?: string; // lets a <label htmlFor> target the underlying <select>
  items: SelectMenuStandardItem[];
  onSelect: (value: string) => void;
  selectedValue: string | undefined | null;
  placeholder?: string;
  height?: string; // e.g. "38px"
}) {
  // Check if value is null/undefined to determine if we need to show placeholder
  const hasValue = selectedValue !== undefined && selectedValue !== null && selectedValue !== "";

  return (
    <div className={`grid grid-cols-1`}>
      <select
        id={id}
        value={hasValue ? selectedValue : ""}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
          onSelect(e.target.value);
        }}
        className={`rounded-md border-neutral-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 focus:outline-none ${
          hasValue ? "text-neutral-800" : "text-neutral-500"
        } ${height ? "py-0" : ""}`}
        style={height ? { height } : undefined}
      >
        {!hasValue && (
          <option value="" disabled>
            {placeholder || "Select an option"}
          </option>
        )}
        {items.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}

export interface SelectMenuDropdownItem<T> {
  previewLabel?: string;
  label: string;
  value: T;
  isSelected?: boolean;
  isRecommended?: boolean; // Whether to show the light bulb icon
  isDisabled?: boolean; // Optional disabled state
  disabledTooltip?: string; // Tooltip to show when the item is disabled
  hasDivider?: boolean; // Whether to show a divider above this item
  hideFromList?: boolean; // Whether to hide this item from the rendered list when selected (but still show in the dropdown as selectable), useful for "None of the above" type options or selecting non-list data.
}

export function SelectMenuDropdown<T>({
  items,
  onSelect,
  allowMultipleSelected = false,
  isLocked = false,
  lockedItemLeftMargin,
  noSelectedErrorMessage,
  noSelectedErrorMessageShowAsRed = true,
  selectedOverflowItemName,
  rightPositionPx,
  maxWidth,
  dropdownWidthPx,
  isVerticallyStacked = true,
  showAllSelected = false,
  buttonLabel,
  menuMaxHeightPx,
  fullwidth = false, // Whether to make the dropdown take the full width of the parent container
  searchable = false,
  searchPlaceholder = "Search",
}: {
  items: SelectMenuDropdownItem<T>[];
  onSelect: (value: T) => Promise<void>;
  allowMultipleSelected?: boolean;
  isLocked?: boolean;
  lockedItemLeftMargin?: string; // e.g. "ml-[-24px]" (note this only applies if the dropdown is locked)
  noSelectedErrorMessage?: string;
  noSelectedErrorMessageShowAsRed?: boolean; // Whether to show the noSelectedErrorMessage in red text (default true)
  selectedOverflowItemName?: string;
  rightPositionPx?: number;
  maxWidth?: string; // (e.g., "300px")
  dropdownWidthPx?: number;
  isVerticallyStacked?: boolean;
  showAllSelected?: boolean;
  buttonLabel?: string;
  menuMaxHeightPx?: number;
  fullwidth?: boolean; // Whether to make the dropdown take the full width of the parent container
  // When true, renders a search input at the top of the dropdown that filters items by their `label`.
  searchable?: boolean;
  searchPlaceholder?: string;
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const visibleItems = searchable
    ? items.filter((item) => item.label.toLowerCase().includes(searchQuery.trim().toLowerCase()))
    : items;
  // Verify that `items` has either zero or exactly one selected item, otherwise throw an error
  const selectedItems = items.filter((item) => item.isSelected && item.hideFromList !== true);
  if (selectedItems.length > 1 && !allowMultipleSelected) {
    throw new Error(
      "SelectMenu items can only have either zero or one selected item, currently has: " +
        selectedItems.length,
    );
  }

  let showNoSelectedErrorMessage = selectedItems.length === 0 && noSelectedErrorMessage;

  let buttonElement;

  if (showNoSelectedErrorMessage) {
    buttonElement = (
      <span
        className={clsx(noSelectedErrorMessageShowAsRed && "text-red-600", fullwidth && "pl-xxs")}
      >
        {noSelectedErrorMessage}
      </span>
    );
  } else if (buttonLabel) {
    buttonElement = <span className="text-neutral-700">{buttonLabel}</span>;
  } else if (allowMultipleSelected) {
    let selectedItemsToShow = showAllSelected
      ? selectedItems.map((item) => ({
          label: item.previewLabel || item.label,
          recommended: item.isRecommended,
        }))
      : selectedItems.length > 2
      ? [
          {
            label: selectedItems[0].previewLabel || selectedItems[0].label,
            recommended: selectedItems[0].isRecommended,
          },
          {
            label: `+${selectedItems.length - 1} ${selectedOverflowItemName || "reasons"}`,
            recommended: false,
          },
        ]
      : selectedItems.map((item) => ({
          label: item.previewLabel || item.label,
          recommended: item.isRecommended,
        }));

    buttonElement = (
      <div className={`${isVerticallyStacked ? "" : "flex flex-wrap gap-1"}`}>
        {selectedItemsToShow.map((item, idx) => (
          <div
            key={idx}
            className={`px-2.5 py-1.5 ${
              isVerticallyStacked && "w-[145px]"
            } h-[32px] flex justify-center items-center rounded-2xl border border-solid border-neutral-500 font-medium ${
              isVerticallyStacked && idx > 0 ? "mt-1" : ""
            }`}
          >
            {item.recommended && <span className="mr-1 flex items-center">{LightbulbIcon}</span>}
            <span className="text-[14px] text-neutral-700 truncate">{item.label}</span>
          </div>
        ))}
      </div>
    );
  } else {
    buttonElement =
      selectedItems.length === 1 ? selectedItems[0].previewLabel || selectedItems[0].label : null;
  }

  if (isLocked === true) {
    return (
      <div className="inline-block text-left">
        <div>
          <div
            className={clsx("w-full justify-center text-sm text-neutral-500", lockedItemLeftMargin)}
          >
            {buttonElement}
          </div>
        </div>
      </div>
    );
  }

  return (
    <Menu
      as="div"
      className={clsx(
        "inline-block",
        "text-left",
        POLARIS_SELECT_MENU_CLASS,
        fullwidth && "w-full",
      )}
      style={maxWidth ? { maxWidth } : {}}
    >
      {({ open }) => (
        <Float
          placement="bottom-start"
          offset={{
            mainAxis: 4,
            crossAxis: rightPositionPx ? rightPositionPx : 0,
          }}
        >
          <Menu.Button
            className={clsx(
              "inline-flex",
              "w-full",
              fullwidth ? "justify-between" : "justify-center",
              "gap-x-1.5",
              "rounded-md",
              "bg-white",
              "text-sm",
              "font-normal",
              "text-neutral-700",
              "border-none",
              "items-center",
            )}
          >
            {buttonElement}
            <ChevronDownIcon
              className={clsx(
                "h-5 w-5",
                showNoSelectedErrorMessage && noSelectedErrorMessageShowAsRed
                  ? "text-red-600"
                  : "text-neutral-800 hover:rounded-3xl hover:bg-neutral-100",
                // we need to duplicate the hover: styles from the previous line so the hover styles also apply when the menu is open
                open && !showNoSelectedErrorMessage && "rounded-3xl bg-neutral-100",
              )}
              aria-hidden="true"
            />
          </Menu.Button>

          <Menu.Items
            className="rounded-md bg-white shadow-lg focus:outline-none"
            style={{
              zIndex: 250,
              width: dropdownWidthPx ? `${dropdownWidthPx}px` : undefined,
              maxHeight: menuMaxHeightPx ? `${menuMaxHeightPx}px` : undefined,
              overflowY: menuMaxHeightPx ? "auto" : undefined,
            }}
          >
            {searchable && (
              <div
                className="sticky top-0 z-[1] bg-white px-3 pt-2 pb-1"
                onClick={(e) => e.stopPropagation()}
              >
                <input
                  type="text"
                  autoFocus
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.stopPropagation()}
                  placeholder={searchPlaceholder}
                  className="w-full px-2 py-1.5 text-sm border-0 focus:outline-none placeholder-neutral-500"
                />
              </div>
            )}
            <div className="py-1">
              {visibleItems.length === 0 && searchable ? (
                <div className="px-3 py-2 text-sm text-neutral-500">No matches</div>
              ) : null}
              {visibleItems.map((item, idx) => {
                const content = (
                  <React.Fragment key={idx}>
                    {item.hasDivider && <div className="my-2 border-t border-neutral-200" />}
                    <Menu.Item>
                      {({ close }) => {
                        return (
                          <div
                            className={clsx(
                              "flex items-center justify-between px-3",
                              item.isDisabled
                                ? "cursor-not-allowed opacity-50"
                                : "cursor-pointer hover:bg-primary-100",
                            )}
                            onClick={(e) => {
                              if (item.isDisabled) return;
                              e.preventDefault();
                              e.stopPropagation();
                              onSelect(item.value);
                              if (!allowMultipleSelected) {
                                close();
                              }
                            }}
                          >
                            <div className="flex items-center gap-1">
                              {item.isRecommended && (
                                <div
                                  className={clsx(
                                    "ml-2",
                                    item.isSelected ? "text-primary-600" : "text-neutral-500",
                                  )}
                                  style={{
                                    position: "relative",
                                    left: "-5px",
                                  }}
                                >
                                  {LightbulbIcon}
                                </div>
                              )}
                              <div
                                className={clsx(
                                  item.isSelected
                                    ? "text-primary-600 font-semibold"
                                    : "text-neutral-800",
                                  item.isRecommended
                                    ? ""
                                    : items.some((item) => item.isRecommended)
                                    ? "pl-[32px]"
                                    : "pl-[8px]",
                                  "block py-2 text-base",
                                )}
                              >
                                {item.label}
                              </div>
                            </div>
                            <div className="w-6 flex justify-end">
                              {item.isSelected && (
                                <CheckIcon className="w-[20px] h-[20px] text-[#4F46E5]" />
                              )}
                            </div>
                          </div>
                        );
                      }}
                    </Menu.Item>
                  </React.Fragment>
                );

                return item.isDisabled && item.disabledTooltip ? (
                  <ToolTip key={idx} text={item.disabledTooltip}>
                    {content}
                  </ToolTip>
                ) : (
                  <React.Fragment key={idx}>{content}</React.Fragment>
                );
              })}
            </div>
          </Menu.Items>
        </Float>
      )}
    </Menu>
  );
}

export default SelectMenuDropdown;
